// The Route Pass (spec FE-34): the season's road of tiers, XP, Collect buttons and the premium
// unlock. Everything comes from the server: the tiers, the rewards, the XP, and the season's real
// end date (never a countdown). The unlock asks once more, in the page (the frame allows no
// dialogs), and says the price plainly before any coins move.
import { type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { errorText } from '../App';
import { api, type PassState } from '../lib/api';
import { parkNaira } from '../lib/money';
import { useUi } from '../store/ui';
import { S } from '../strings';
import { ScreenTop } from '../ui/ScreenTop';
import { Html, W } from '../ui/wahala';

/** The last day a season runs, in Lagos time: it ends at the start of its end date. */
const lastDay = (endsAt: string) =>
  new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Africa/Lagos' }).format(
    new Date(Date.parse(endsAt) - 1),
  );

export function Pass() {
  const set = useUi((s) => s.set);
  const [pass, setPass] = useState<PassState | null>(null);
  const road = useRef<HTMLDivElement>(null);
  const placed = useRef(false);
  // open at your place on the road: the first reward ready to collect, else the next tier to reach
  useEffect(() => {
    if (placed.current || !pass?.tiers || !road.current) return;
    const at =
      road.current.querySelector('.wh-pass__cell.is-ready') ??
      road.current.querySelector('.wh-pass__tier:not(.is-reached)');
    if (!at) return;
    placed.current = true;
    at.scrollIntoView({ block: 'center' });
  }, [pass]);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);

  const show = useCallback(
    (p: PassState) => {
      setPass(p);
      if (p.coins !== undefined) set({ coins: p.coins });
    },
    [set],
  );

  useEffect(() => {
    api
      .pass()
      .then(show)
      .catch((e) => set({ notice: errorText(e) }));
  }, [set, show]);

  async function run(f: () => Promise<PassState>, done: string) {
    setBusy(true);
    set({ notice: null });
    try {
      show(await f());
      set({ notice: done, noticeTone: 'done' });
    } catch (e) {
      set({ notice: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  const season = pass?.season;

  function onBoard(e: MouseEvent<HTMLDivElement>) {
    if (!season || busy) return;
    const el = e.target as HTMLElement;
    if (el.closest('[data-unlock]')) {
      setAsking(true);
      return;
    }
    const claim = el.closest('[data-claim]')?.getAttribute('data-claim');
    if (!claim) return;
    const [track, tier] = claim.split(':');
    if (track !== 'free' && track !== 'premium') return;
    void run(() => api.passClaim(season.id, track, Number(tier)), S.pass.collected);
  }

  return (
    <main className="screen shop-screen pass-screen" data-theme="day">
      <ScreenTop title={S.menu.pass} />

      {!pass && <p className="boot">{S.pass.loading}</p>}
      {pass && !season && <p className="card">{S.pass.none}</p>}

      {season && asking && (
        <section className="card stack" role="alertdialog" aria-labelledby="unlock-q">
          <p id="unlock-q">{S.pass.confirm(parkNaira(season.premiumPrice))}</p>
          <div className="row">
            <button
              type="button"
              className="wh-btn wh-btn--carry wh-btn--md"
              disabled={busy}
              onClick={() => {
                setAsking(false);
                void run(() => api.passUnlock(season.id), S.pass.opened);
              }}
            >
              {S.pass.confirmYes}
            </button>
            <button type="button" className="wh-btn wh-btn--quiet wh-btn--md" onClick={() => setAsking(false)}>
              {S.pass.confirmNo}
            </button>
          </div>
        </section>
      )}

      {season && pass?.tiers && (
        <Html
          ref={road}
          onClick={onBoard}
          html={W().routePass({
            title: S.pass.title,
            season: season.name,
            ends: season.running ? S.pass.runs(lastDay(season.endsAt)) : S.pass.ended(lastDay(season.endsAt)),
            running: season.running,
            xp: pass.xp ?? 0,
            tier: pass.tier ?? 0,
            premium: pass.premium ?? false,
            premiumPrice: season.premiumPrice,
            coins: pass.coins ?? 0,
            tiers: pass.tiers as never,
            player: 'danfo',
            disabled: busy || asking,
            note: S.pass.note,
          })}
        />
      )}
    </main>
  );
}
